import {
  addDays,
  differenceInDays,
  formatISO,
  parseISO,
  startOfDay,
  startOfToday,
  startOfYesterday,
} from "date-fns";

import queryString from "query-string";

import { CONNECTION_GAMES } from "./data";

export const getToday = () => startOfToday();
export const getYesterday = () => startOfYesterday();

const getSortedPuzzleDateKeys = () => {
  return Object.keys(CONNECTION_GAMES)
    .filter((dateString) => {
      const parsedDate = parseISO(dateString);
      return !Number.isNaN(parsedDate.getTime());
    })
    .sort((a, b) => a.localeCompare(b));
};

const getResolvedPuzzleDateKey = (gameDate = getToday()) => {
  const requestedDateKey = formatISO(startOfDay(gameDate), {
    representation: "date",
  });
  const todayKey = formatISO(getToday(), { representation: "date" });
  const sortedPuzzleKeys = getSortedPuzzleDateKeys();

  if (CONNECTION_GAMES[requestedDateKey]) {
    return requestedDateKey;
  }

  if (CONNECTION_GAMES[todayKey]) {
    return todayKey;
  }

  return sortedPuzzleKeys[0] || todayKey;
};

// October 2023 Game Epoch
// https://stackoverflow.com/questions/2552483/why-does-the-month-argument-range-from-0-to-11-in-javascripts-date-constructor
export const firstGameDate = new Date(2025, 0, 4);
export const periodInDays = 1;

export const getLastGameDate = (today) => {
  const t = startOfDay(today);
  let daysSinceLastGame = differenceInDays(t, firstGameDate) % periodInDays;
  return addDays(t, -daysSinceLastGame);
};

export const getNextGameDate = (today) => {
  return addDays(getLastGameDate(today), periodInDays);
};

export const isValidGameDate = (date) => {
  if (date < firstGameDate || date > getToday()) {
    return false;
  }

  return differenceInDays(firstGameDate, date) % periodInDays === 0;
};

export const getIndex = (gameDate) => {
  const resolvedDateKey = getResolvedPuzzleDateKey(gameDate);
  const sortedPuzzleKeys = getSortedPuzzleDateKeys();
  const resolvedPuzzleIndex = sortedPuzzleKeys.indexOf(resolvedDateKey);
  return resolvedPuzzleIndex >= 0 ? resolvedPuzzleIndex + 1 : 1;
};

export const getPuzzleOfDay = (gameDate = getToday()) => {
  const resolvedDateKey = getResolvedPuzzleDateKey(gameDate);
  return CONNECTION_GAMES[resolvedDateKey] || Object.values(CONNECTION_GAMES)[0];
};

export const getArchivePuzzleDates = () => {
  const today = startOfDay(getToday());
  const normalizedArchiveDates = Object.keys(CONNECTION_GAMES)
    .map((dateString) => {
      const parsedDate = parseISO(dateString);
      if (Number.isNaN(parsedDate.getTime())) {
        return null;
      }
      return startOfDay(parsedDate);
    })
    .filter((date) => date && date <= today)
    .map((date) => formatISO(date, { representation: "date" }));

  return Array.from(new Set(normalizedArchiveDates)).sort((a, b) =>
    b.localeCompare(a)
  );
};

export const getArchiveGameHref = (dateString) => {
  return "/?d=" + encodeURIComponent(dateString);
};

export const isTodayPuzzleDate = (dateString) => {
  if (!dateString) {
    return false;
  }
  try {
    const parsedDate = parseISO(dateString);
    if (Number.isNaN(parsedDate.getTime())) {
      return false;
    }
    return startOfDay(parsedDate).getTime() === startOfDay(getToday()).getTime();
  } catch (error) {
    return false;
  }
};

export const getSolution = (gameDate) => {
  const nextGameDate = getNextGameDate(gameDate);
  const index = getIndex(gameDate);
  const puzzleOfTheDay = getPuzzleOfDay(gameDate);
  console.log("index for today: ", index);
  return {
    puzzleAnswers: puzzleOfTheDay,
    puzzleGameDate: gameDate,
    puzzleIndex: index,
    dateOfNextPuzzle: nextGameDate.valueOf(),
  };
};

export const getGameDate = () => {
  if (getIsLatestGame()) {
    return getToday();
  }

  const parsed = queryString.parse(window.location.search);
  try {
    const requestedDate = parsed.d?.toString();
    if (!requestedDate) {
      return getToday();
    }
    const parsedDate = parseISO(requestedDate);
    if (Number.isNaN(parsedDate.getTime())) {
      return getToday();
    }
    const d = startOfDay(parsedDate);
    if (!isValidGameDate(d)) {
      return getToday();
    }
    return d;
  } catch (e) {
    console.log(e);
    return getToday();
  }
};

export const setGameDate = (d) => {
  try {
    if (d < getToday()) {
      window.location.href = "/?d=" + formatISO(d, { representation: "date" });
      return;
    }
  } catch (e) {
    console.log(e);
  }
  window.location.href = "/";
};

export const getIsLatestGame = () => {
  // https://github.com/cwackerfuss/react-wordle/pull/505
  const parsed = queryString.parse(window.location.search);
  return parsed === null || !("d" in parsed);
};

export const getCurrentPuzzleSolution = () => getSolution(getGameDate());

export const { puzzleAnswers, puzzleGameDate, puzzleIndex, dateOfNextPuzzle } =
  getCurrentPuzzleSolution();
